/**
 * @fileoverview AR-924 — "Add a link" of the class upload: URL + optional students. Local copy of the
 * drawer's AddLinkModal (project rule: copy per module).
 */

import { useEffect, useState } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'
import { Button, Group, Modal, MultiSelect, Stack, TextInput } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { normalizeLinkUrl } from '../deliverableDrawer/linkUtils'
import { resolveStudentSelection, selectValueOf, withAllStudentsOption } from './allStudentsOption'

const LINK_URL_MAX_LENGTH = 2048
/** Above the "Upload for class" modal (Mantine default 200); the select dropdown must sit above this one. */
const MODAL_Z_INDEX = 400
const DROPDOWN_Z_INDEX = MODAL_Z_INDEX + 1

interface AddLinkForStudentModalProps {
  readonly opened: boolean
  readonly studentOptions: { value: string; label: string }[]
  readonly onClose: () => void
  readonly onAdd: (url: string, studentIds: readonly string[]) => void
}

export function AddLinkForStudentModal({ opened, studentOptions, onClose, onAdd }: AddLinkForStudentModalProps) {
  const { t } = useTranslation()
  const tr = (key: string) => t(`task_tracker.deliverable_drawer.${key}`)
  const [value, setValue] = useState('')
  const [studentIds, setStudentIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const everyone = studentOptions.map(option => option.value)

  useEffect(() => {
    if (!opened) return
    setValue('')
    // A single uploadable student needs no choice.
    setStudentIds(studentOptions.length === 1 ? [studentOptions[0].value] : [])
    setError(null)
  }, [opened, studentOptions])

  function submit() {
    const normalized = normalizeLinkUrl(value)
    if (!normalized) {
      setError(tr('link_invalid'))
      return
    }
    onAdd(normalized, studentIds)
    onClose()
  }

  function handleUrlChange(event: ChangeEvent<HTMLInputElement>) {
    setValue(event.currentTarget.value)
    setError(null)
  }

  function handleUrlKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    submit()
  }
  const allowImplicitClose = value.trim() === ''
  const canAdd = normalizeLinkUrl(value) !== null

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={tr('add_link_modal_title')}
      centered
      zIndex={MODAL_Z_INDEX}
      closeOnClickOutside={allowImplicitClose}
      closeOnEscape={allowImplicitClose}
    >
      <Stack spacing="md">
        <TextInput
          label={tr('link_url_label')}
          placeholder={tr('link_url_placeholder')}
          value={value}
          onChange={handleUrlChange}
          error={error}
          maxLength={LINK_URL_MAX_LENGTH}
          data-autofocus
          onKeyDown={handleUrlKeyDown}
        />
        <MultiSelect
          label={t('task_tracker.upload_for_class.col_student')}
          placeholder={t('task_tracker.upload_for_class.select_student')}
          data={withAllStudentsOption(studentOptions, t('task_tracker.upload_for_class.all_students'))}
          value={selectValueOf(studentIds, everyone)}
          onChange={next => setStudentIds(resolveStudentSelection(studentIds, next, everyone))}
          searchable
          clearable
          withinPortal
          zIndex={DROPDOWN_Z_INDEX}
        />
        <Group position="right" spacing="xs">
          <Button variant="default" onClick={onClose}>{tr('cancel')}</Button>
          <Button onClick={submit} disabled={!canAdd}>{tr('add')}</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
